import React, { useRef, useEffect, useState } from 'react';
import { useTableManager } from './useTableManager';
import { OCVTableProps } from './OCVTypes';
import { CopyIcon } from '../../GenericComponents/copyIcon';
import { useOCVDataProcessor } from './useOCVDataProcessor';
import { processDisplayData } from './OCVUtils';
import { getBaseUrl } from '../../Utils';

export const OCVTable: React.FC<OCVTableProps> = ({
  tableKey,
  data,
  metadata,
  isFixedSection,
  baseUrl,
  launcherMode
}) => {

  const {
    state,
    setState,
    paginatedData,
    handleSort,
    handlePageChange,
    handlePageSizeChange
  } = useTableManager(data);

  const [loading, setLoading] = useState(false);
  const { processDynamicSection } = useOCVDataProcessor();
  const [fixedSectionHeight, setFixedSectionHeight] = useState<number>(0);
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleDynamicContent = async () => {
      if (!data.isDynamic || !metadata?.ajaxOpts) return;

      setLoading(true);
      try {
        const result = await processDynamicSection(metadata, tableKey);
        if (result) {
          const handlerName = metadata.ajaxOpts.successHandler?.split('.').pop() || 'displayTableFromJSON';
          const processor = processDisplayData(result);
          const processedData = processor[handlerName as keyof typeof processor]();
          const isEmptyResponse = !processedData.Item2 || processedData.Item2.length === 0;

          setState(prev => ({
            ...prev,
            Item1: processedData.Item1 || [],
            Item2: processedData.Item2 || [],
            totalRows: processedData.Item2.length,
            isEmpty: isEmptyResponse
          }));
        }
      } catch (error) {
        console.error('Error loading dynamic content:', error);
        setState(prev => ({
          ...prev,
          error: 'Sorry, requested object could not be retrieved. Error notification has been sent to IT for assistance.'
        }));
      } finally {
        setLoading(false);
      }
    };

    if (data.isDynamic && data.ajaxOptions) {
      handleDynamicContent();
    }
  }, [data.isDynamic, metadata, tableKey]);


  const getCellContent = (cell: any): string => {
    if (cell === null || cell === undefined) {
      return "";
    }

    if (typeof cell === "object") {
      // Check if object is empty
      if (Object.keys(cell).length === 0) {
        return "";
      }
      // Convert object to string representation
      return JSON.stringify(cell);
    }

    return cell.toString();
  };


  // ResizeObserver for fixed sections
  useEffect(() => {
    if (isFixedSection && tableRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (tableRef.current) {
          setFixedSectionHeight(tableRef.current.offsetHeight);
        }
      });

      resizeObserver.observe(tableRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [isFixedSection]);

  const getPageSizeOptions = (totalRows: number): number[] => {
    const options: number[] = [10];

    if (totalRows <= 50) {
      for (let i = 20; i <= Math.min(Math.floor(totalRows / 10) * 10, 50); i += 10) {
        options.push(i);
      }
    }
    else if (totalRows <= 100) {
      options.push(20, 30, 40, 50);
    }
    else if (totalRows <= 200) {
      options.push(20, 30, 40, 50, 100);
    }
    else {
      options.push(20, 30, 40, 50, 100, 200);
    }

    return options;
  };
  const shouldShowHeader = !(
    (data.isDynamic && (state.isEmpty || loading)) || // Hide during loading and empty state for dynamic
    (!data.isDynamic && state.isEmpty) // Hide for empty state in non-dynamic
  );


  return (
    <>
      <h1>{tableKey}</h1>

      <div
        ref={tableRef}
        className={`vu-table-wrapper tablesorter-pager ${isFixedSection ? 'ocv_fixed_section' : ''}`}
        style={isFixedSection ? { height: `${fixedSectionHeight}px` } : undefined}
      >


        <table className="vu-table focus-highlight hover-highlight">
          {shouldShowHeader && (
            <thead>
              <tr className="vu-header-row-center">
                {data.Item1.map((column, index) => (
                  <th
                    key={index}
                    onClick={() => handleSort(index)}
                    className="vu-fixed-cell vu-mark-cell vu-sort-cell"
                  >
                    <div className="tablesorter-header-inner">
                      {column}
                      {state.sortColumn === index && (
                        <span className={`sort-indicator ${state.sortDirection}`}>
                          {state.sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>

            {loading ? (
              <tr>
                <td colSpan={data.Item1.length || 1} className="vu-data-row">
                  <div className="ajax-loading-box">
                    <span className="ajax-loading-image"></span>Loading...
                  </div>
                </td>
              </tr>
            ) : state.error ? (
              <tr>
                <td colSpan={data.Item1.length || 1} className="vu-data-row">
                  <div className="error-message">{state.error}</div>
                </td>
              </tr>
            ) : state.isEmpty ? (
              <tr>
                <td colSpan={data.Item1.length || 1} className="vu-data-row">
                  <div className="no-data-message">No Data</div>
                </td>
              </tr>
            )
              : launcherMode === "GenericFlat" || launcherMode === "flat" ?
                (
                  data.Item2.map((row, rowIndex) => (
                    <tr key={rowIndex} className="vu-data-row">
                      {row.map((cell, cellIndex) => (

                        <td
                          key={cellIndex}
                          className="vu-fixed-cell vu-mark-cell"
                        >
                          {data.Item4[0] && cellIndex === 0 ? (
                            <a href={`${baseUrl}/API/v3/ALobject/ALObjectByFileName?filename=${row[0]}`}>
                              {row[1]}
                            </a>
                          ) : (
                            <>
                              {typeof cell === 'string' && cell.startsWith('<a href') ? (
                                <>
                                  <span dangerouslySetInnerHTML={{ __html: cell }} />
                                  {/* Extract text between > and < to get just the link text */}
                                  {cell.match(/>([^<]+)</)?.[1] &&
                                    <CopyIcon copyText={cell.match(/>([^<]+)</)?.[1] || ''} id="" />
                                  }
                                </>
                              ) : (
                                <>
                                  {getCellContent(cell)}
                                  {getCellContent(cell) !== '' && getCellContent(cell) !== 'No Data' &&
                                    <CopyIcon copyText={getCellContent(cell)} id="" />
                                  }
                                </>
                              )}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="vu-data-row">
                      {row.map((cell, cellIndex) => (

                        <td
                          key={cellIndex}
                          className="vu-fixed-cell vu-mark-cell"
                        >
                          {data.Item4[0] && cellIndex === 0 ? (
                            <a href={`${baseUrl}/API/v3/ALobject/ALObjectByFileName?filename=${row[0]}`}>
                              {row[1]}
                            </a>
                          ) : (
                            <>
                              {typeof cell === 'string' && cell.startsWith('<a href') ? (
                                <>
                                  <span dangerouslySetInnerHTML={{ __html: cell }} />
                                  {/* Extract text between > and < to get just the link text */}
                                  {cell.match(/>([^<]+)</)?.[1] &&
                                    <CopyIcon copyText={cell.match(/>([^<]+)</)?.[1] || ''} id="" />
                                  }
                                </>
                              ) : (
                                <>
                                  {getCellContent(cell)}
                                  {getCellContent(cell) !== '' && getCellContent(cell) !== 'No Data' &&
                                    <CopyIcon copyText={getCellContent(cell)} id="" />
                                  }
                                </>
                              )}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )
            }
          </tbody>
        </table>


        {/* Pagination Controls */}
        {!loading && !state.error && launcherMode !== "GenericFlat" && launcherMode !== "flat" && paginatedData.length > 0 && paginatedData[0].some(cell => cell) &&
          data.Item2.length > 10 ? (
          <div id={`vu_paging_wrapper_${tableKey}`} className="vu-paging-wrapper tablesorter-pager">
            <div className="right">
              <img
                src={`${getBaseUrl()}/images/first.png`}
                title="First Page"
                className={`first ${state.currentPage === 0 || state.showAllRows ? 'disabled' : ''}`}
                onClick={() => !state.showAllRows && state.currentPage !== 0 && handlePageChange(0)}
                tabIndex={0}
                aria-disabled={state.currentPage === 0 || state.showAllRows}
                style={{ cursor: state.currentPage === 0 || state.showAllRows ? 'not-allowed' : 'pointer' }}
              />

              <img
                src={`${getBaseUrl()}/images/prev.png`}
                title="Previous Page"
                className={`prev ${state.currentPage === 0 || state.showAllRows ? 'disabled' : ''}`}
                onClick={() => !state.showAllRows && state.currentPage !== 0 && handlePageChange(state.currentPage - 1)}
                tabIndex={0}
                aria-disabled={state.currentPage === 0 || state.showAllRows}
                style={{ cursor: state.currentPage === 0 || state.showAllRows ? 'not-allowed' : 'pointer' }}
              />
              <span
                className="pagedisplay"
                style={{ verticalAlign: 'top' }}
                id={`vu_table_${tableKey}_pager_info`}
              >
                Showing rows: {state.currentPage * state.pageSize + 1} to {
                  Math.min((state.currentPage + 1) * state.pageSize, state.totalRows)
                } (Total: {state.totalRows})
              </span>
              <img
                src={`${getBaseUrl()}/images/next.png`}
                title="Next Page"
                className={`next ${(state.currentPage + 1) * state.pageSize >= state.totalRows ? 'disabled' : ''}`}
                onClick={() => handlePageChange(state.currentPage + 1)}
                tabIndex={0}
                aria-disabled={(state.currentPage + 1) * state.pageSize >= state.totalRows}
                style={{ cursor: 'pointer' }}
              />
              <img
                src={`${getBaseUrl()}/images/last.png`}
                title="Last Page"
                className={`last ${(state.currentPage + 1) * state.pageSize >= state.totalRows ? 'disabled' : ''}`}
                onClick={() => handlePageChange(Math.ceil(state.totalRows / state.pageSize) - 1)}
                tabIndex={0}
                aria-disabled={(state.currentPage + 1) * state.pageSize >= state.totalRows}
                style={{ cursor: 'pointer' }}
              />
              <select
                className="pagesize"
                title="Select page size"
                value={state.showAllRows ? 'all' : state.pageSize}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    handlePageSizeChange(state.totalRows, true);
                  } else {
                    const newSize = parseInt(value, 10);
                    if (newSize > 0) {
                      handlePageSizeChange(newSize, false);
                    }
                  }
                }}
                aria-disabled={false}
                style={{ cursor: 'pointer', zIndex: 3 }}
              >
                {getPageSizeOptions(state.totalRows).map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
                <option value="all">All Rows</option>
              </select>
              <select
                className="gotoPage"
                title="Select page number"
                value={state.currentPage + 1}
                onChange={(e) => handlePageChange(Number(e.target.value) - 1)}
                disabled={state.showAllRows}
                style={{
                  cursor: state.showAllRows ? 'not-allowed' : 'pointer',
                  display: state.showAllRows ? 'none' : 'inline-block'
                }}
              >
                {Array.from(
                  { length: Math.ceil(state.totalRows / state.pageSize) },
                  (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
        ) : (
          <div id={`vu_paging_wrapper_${tableKey}`} className="vu-paging-wrapper tablesorter-pager">
            <div className="right">
              <span className="padding-left-largest">
                {state.Item2[0]?.[0] === 'No Data' || loading ? (
                  'Showing rows: 0'
                ) : (
                  `Showing rows: ${state.Item2.length}`
                )}
              </span>
            </div>
          </div>
        )}

      </div>
    </>
  );
};